import styled from "@emotion/styled"
import type { BreakpointValues } from "@porsche-design-system/components-react"
import { PInlineNotification, PLinkPure, PText } from "@porsche-design-system/components-react"
import { spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { SalesActivitySaleAndDeliveryView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useMemo } from "react"
import type { ReactElement } from "react"
import { FormattedMessage, defineMessages, useIntl } from "react-intl"
import { Link } from "react-router"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { commonMessages } from "src/common/messages/common.messages"
import type { GridItemSize } from "src/components"
import { Grid, GridItem, List } from "src/components"

const messages = defineMessages({
	deliveryInformationDeliveryDate: {
		defaultMessage: "Delivery date",
		id: "vehicleDetails.salesActivity.saleAndDelivery.deliveryInformation.deliveryDate",
	},
	deliveryInformationHeadline: {
		defaultMessage: "Delivery",
		id: "vehicleDetails.salesActivity.saleAndDelivery.deliveryInformation.headline",
	},
	openSaleDetails: {
		defaultMessage: "Open sale details",
		id: "vehicleDetails.salesActivity.saleAndDelivery.saleDetailsInformation.action",
	},
	saleDetailsInformationDescription: {
		defaultMessage: "For new vehicles, sales and delivery information is managed on the sale details page.",
		id: "vehicleDetails.salesActivity.saleAndDelivery.saleDetailsInformation.description",
	},
	saleDetailsInformationHeading: {
		defaultMessage: "Sales information available in sale details",
		id: "vehicleDetails.salesActivity.saleAndDelivery.salesDetailInformation.heading",
	},
	salesInformationCustomerHeadline: {
		defaultMessage: "Customer",
		id: "vehicleDetails.salesActivity.saleAndDelivery.salesInformation.customer.headline",
	},
	salesInformationHeadline: {
		defaultMessage: "Sale",
		id: "vehicleDetails.salesActivity.saleAndDelivery.salesInformation.headline",
	},
	salesInformationOnlineOrderHeadline: {
		defaultMessage: "Online Order",
		id: "vehicleDetails.salesActivity.saleAndDelivery.salesInformation.onlineOrder.headline",
	},
	salesInformationOnlineOrderLink: {
		defaultMessage: "Open order details",
		id: "vehicleDetails.salesActivity.saleAndDelivery.salesInformation.onlineOrder.link",
	},
	salesInformationSalesDate: {
		defaultMessage: "Sales date",
		id: "vehicleDetails.salesActivity.saleAndDelivery.salesInformation.salesDate",
	},
	salesInformationSalesPerson: {
		defaultMessage: "Sales person",
		id: "vehicleDetails.salesActivity.saleAndDelivery.salesInformation.salesPerson",
	},
	salesInformationSalesPrice: {
		defaultMessage: "Sales price",
		id: "vehicleDetails.salesActivity.saleAndDelivery.salesInformation.salesPrice",
	},
	sellableVehicleHint: {
		defaultMessage: "The vehicle is not sold yet",
		id: "vehicleDetails.salesActivity.saleAndDelivery.sellableVehicleHint",
	},
})

interface SaleAndDeliveryProps {
	customerName?: string
	onLinkClick: (item: string) => void
	renderPorscheApprovedWarrantyDuration?: (
		porscheApprovedSale: NonNullable<SalesActivitySaleAndDeliveryView["salesInformation"]>["porscheApprovedSale"],
	) => ReactElement
	vehicleKey: string
	view: SalesActivitySaleAndDeliveryView
}

const gridSize: BreakpointValues<GridItemSize> | GridItemSize = { base: 12, l: 6 }

export const SaleAndDelivery = (props: SaleAndDeliveryProps) => {
	const { customerName, onLinkClick, renderPorscheApprovedWarrantyDuration, vehicleKey, view } = props
	const { deliveryAction, deliveryInformation, onlineOrderInformation, salesAction, salesInformation } = view
	const { formatMessage } = useIntl()
	const routes = useDealerAppRoutes()
	const saleId = salesInformation?.saleId
	const saleDetailsUrl = saleId !== undefined ? routes?.salesList?.saleDetails?.url(saleId) : undefined

	const displayedCustomerName = useMemo(
		() => customerName ?? salesInformation?.customerName,
		[customerName, salesInformation?.customerName],
	)

	const onOnlineOrderLinkClick = useCallback(() => onLinkClick("salesAndDelivery.onlineOrder"), [onLinkClick])
	const onSellVehicleLinkClick = useCallback(() => onLinkClick("salesAndDelivery.sellVehicle"), [onLinkClick])
	const onDeliverVehicleLinkClick = useCallback(() => onLinkClick("salesAndDelivery.deliverVehicle"), [onLinkClick])

	return (
		<Grid data-testid="sale-and-delivery" $rowGap={spacingStaticMd}>
			{saleId !== undefined ? (
				<GridItem size={{ base: 12, l: 8 }}>
					<PInlineNotification
						data-testid="sale-and-delivery_sale-details-information"
						dismissButton={false}
						heading={formatMessage(messages.saleDetailsInformationHeading)}
						headingTag="h2"
						state="info"
					>
						<NotificationContent>
							<FormattedMessage {...messages.saleDetailsInformationDescription} />
							{saleDetailsUrl && (
								<PLinkPure icon="none" underline>
									<Link to={saleDetailsUrl}>
										<FormattedMessage {...messages.openSaleDetails} />
									</Link>
								</PLinkPure>
							)}
						</NotificationContent>
					</PInlineNotification>
				</GridItem>
			) : (
				<>
					<GridItem size={gridSize}>
						{displayedCustomerName ? (
							<List header={formatMessage(messages.salesInformationHeadline)}>
								{onlineOrderInformation && (
									<List.Item
										label={formatMessage(messages.salesInformationOnlineOrderHeadline)}
										value={
											onlineOrderInformation.onlineOrderLink && (
												<PLinkPure
													icon="none"
													underline
													href={onlineOrderInformation.onlineOrderLink.href}
													target="_blank"
													onClick={onOnlineOrderLinkClick}
												>
													<FormattedMessage {...messages.salesInformationOnlineOrderLink} />
												</PLinkPure>
											)
										}
									/>
								)}
								<List.Item
									label={formatMessage(messages.salesInformationCustomerHeadline)}
									value={displayedCustomerName}
								/>

								{(salesInformation?.salesPrice ??
									salesInformation?.salesDate ??
									salesInformation?.salesPerson) && (
									<>
										{salesInformation.salesPrice && (
											<List.Item
												label={formatMessage(messages.salesInformationSalesPrice)}
												value={salesInformation.salesPrice}
											/>
										)}
										{salesInformation.salesDate && (
											<List.Item
												label={formatMessage(messages.salesInformationSalesDate)}
												value={salesInformation.salesDate}
											/>
										)}
										{salesInformation.salesPerson && (
											<List.Item
												label={formatMessage(messages.salesInformationSalesPerson)}
												value={salesInformation.salesPerson}
											/>
										)}
										{renderPorscheApprovedWarrantyDuration && (
											<List.Item
												label={formatMessage(commonMessages.porscheApproved)}
												value={renderPorscheApprovedWarrantyDuration(
													salesInformation?.porscheApprovedSale,
												)}
											/>
										)}
									</>
								)}
							</List>
						) : (
							<>
								<PText>
									<FormattedMessage {...messages.sellableVehicleHint} />
								</PText>
								{salesAction?.disabledHint === undefined &&
									salesAction?.label &&
									routes?.inventoryList?.vehicleDetails?.sell?.url && (
										<PLinkPure icon="none" underline>
											<Link
												data-testid="sale-and-delivery_sell-link"
												to={routes.inventoryList.vehicleDetails.sell.url(vehicleKey)}
												onClick={onSellVehicleLinkClick}
											>
												{salesAction.label}
											</Link>
										</PLinkPure>
									)}
							</>
						)}
					</GridItem>

					{deliveryInformation && (
						<GridItem size={gridSize}>
							<List header={formatMessage(messages.deliveryInformationHeadline)}>
								<List.Item
									label={formatMessage(messages.deliveryInformationDeliveryDate)}
									value={
										deliveryInformation.deliveryDate ? (
											<PText>{deliveryInformation.deliveryDate}</PText>
										) : deliveryAction?.disabledHint === undefined &&
											deliveryInformation.deliverVehicleLabel &&
											routes?.inventoryList?.vehicleDetails?.deliver ? (
											<PLinkPure icon="none" underline>
												<Link
													data-testid="sale-and-delivery_deliver-link"
													to={routes.inventoryList.vehicleDetails.deliver.url(vehicleKey)}
													onClick={onDeliverVehicleLinkClick}
												>
													{deliveryInformation.deliverVehicleLabel}
												</Link>
											</PLinkPure>
										) : undefined
									}
								/>
							</List>
						</GridItem>
					)}
				</>
			)}
		</Grid>
	)
}

const NotificationContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: start;
`
